import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import queryString from 'query-string';

import { MY_ACCOUNT_DATE_FORMAT } from 'constants/appConstants';
import { HYDRA_6PM_MARTY_RETURNS, HYDRA_ORDER_DETAIL_FIT_SURVEY, HYDRA_ZEN_RETURNS } from 'constants/hydraTests';
import { evCancelCheckedItemsClick, pvOrderDetail } from 'events/account';

import {
  removeItemForCancellationOrReturn,
  resetCancelOrReturnItems,
  selectItemForCancellationOrReturn
} from 'actions/account/account';
import { fetchOrderDetails, redirectToLegacyReturns } from 'actions/account/order';
import { resetShipmentTracking } from 'actions/account/shipmentTracking';
import {
  stepSelection,
  updateBoxNumInfo
} from 'actions/account/returns';
import { checkCustomerAuthentication } from 'actions/authentication';
import { isAssigned, isInAssignment, triggerAssignment } from 'actions/ab';
import { fitSurveyReply, getCustomerFits, updateFitSurvey } from 'actions/landing/newsfeed';
import { firePixelServer } from 'actions/pixelServer';
import OrderTableLayout from 'components/account/Order/OrderTableLayout';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { PageLoader } from 'components/Loader';
import OrderStatus from 'components/account/OrderStatus';
import BackNav from 'components/account/BackNav';
import Breadcrumb from 'components/account/Breadcrumb';
import AccountHeader from 'containers/account/AccountHeader';
import OrderCare from 'containers/account/OrderCare';
import OrderInformation from 'components/account/Order/OrderInformation';
import OrderSummary from 'components/account/Order/OrderSummary';
import CancelOrReturnItemSelect from 'components/account/Order/CancelOrReturnItemSelect';
import ReturnsAndExchanges from 'components/account/ReturnsAndExchanges';
import { titaniteView, track } from 'apis/amethyst';
import css from 'styles/containers/account/order.scss';

export class Order extends Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    testId: PropTypes.func,
    marketplace: PropTypes.object
  };

  state = {
    isFitSurveyLoading: false,
    sameOrderPaymentUpdated: false, // show paymentUpdate success message after order data refresh
    cancelItemsMobileVisible: false,
    returnItemsMobileVisible: false
  };

  componentDidMount() {
    titaniteView();

    const {
      params: { orderId },
      newsfeed: { fits },
      firePixelServer,
      getCustomerFits,
      checkCustomerAuthentication,
      triggerAssignment
    } = this.props;
    const returnTo = `/orders/${orderId}`;

    triggerAssignment(HYDRA_6PM_MARTY_RETURNS);
    triggerAssignment(HYDRA_ZEN_RETURNS);
    const hydraOrderDetailFitSurvey = triggerAssignment(HYDRA_ORDER_DETAIL_FIT_SURVEY);

    if (isInAssignment(hydraOrderDetailFitSurvey) && !fits.length) {
      getCustomerFits();
    }

    checkCustomerAuthentication(returnTo, () => this.fetchData());
    firePixelServer('order-info');
  }

  componentDidUpdate(prevProps) {
    const { order: { order: prevOrder } } = prevProps;
    const { order: { paymentUpdated, order } } = this.props;

    if (prevOrder?.orderId === order?.orderId && paymentUpdated && !this.state.sameOrderPaymentUpdated) { // if same order and paymentUpdated
      const forceRefresh = true;
      this.setState({ sameOrderPaymentUpdated: true });
      this.fetchData(forceRefresh);
    }
  }

  fetchData(forceRefresh = false) {
    const {
      order: { order },
      params: { orderId },
      account: { cancelOrReturnItems: items },
      shipmentTracking: { trackingInfo },
      fetchOrderDetails,
      resetCancelOrReturnItems,
      resetShipmentTracking
    } = this.props;

    if (items?.length) {
      resetCancelOrReturnItems();
    }

    if (trackingInfo?.length) {
      resetShipmentTracking();
    }

    // Check if cached order is different from requested order
    const staleData = order && (orderId !== order.orderId);
    // Fetch only if the order data is not loaded or stale or forceRefresh flag is true
    if (!order || staleData || forceRefresh) {
      Promise.resolve(fetchOrderDetails(orderId, true)).then(response => {
        if (response) {
          track(pvOrderDetail, response);
        }
      });
    }
  }

  handleToggleItemSelection = (lineItem, orderId) => {
    const {
      account: { cancelOrReturnItems: items },
      selectItemForCancellationOrReturn,
      removeItemForCancellationOrReturn
    } = this.props;

    if (items.find(item => item === lineItem)) {
      removeItemForCancellationOrReturn(lineItem);
    } else {
      lineItem.orderId = orderId;
      selectItemForCancellationOrReturn(lineItem);
    }
  };

  handleCancelItems = () => {
    const { account: { cancelOrReturnItems: items }, params: { orderId } } = this.props;
    const lineItemIds = queryString.stringify({ lineItemId: items.map(item => item.id) });
    track(evCancelCheckedItemsClick, { orderId, items });
    this.context.router.pushPreserveAppRoot(`/orders/${orderId}/cancel?${lineItemIds}`);
  };

  handleReturnItems = () => {
    const { account: { cancelOrReturnItems: items }, returns: { boxInfo }, updateBoxNumInfo, stepSelection } = this.props;
    const { router, marketplace: { account: { multiBoxReturns } } } = this.context;

    updateBoxNumInfo(1, boxInfo, items); // initialize 1 box default in store
    if (items.length === 1 || !multiBoxReturns) {
      stepSelection('SELECT_LABEL');
      router.pushPreserveAppRoot('/return');
    } else {
      stepSelection('SELECT_BOX');
      router.pushPreserveAppRoot('/returns');
    }
  };

  handleReturnItemsLegacy = () => {
    const { account: { cancelOrReturnItems: items }, redirectToLegacyReturns } = this.props;
    const productsList = items.map(item => `&asin=${item.product.asin}`).join('');
    redirectToLegacyReturns(productsList);
  }

  handleSelectFitQuestion = ({ currentTarget }) => {
    const { fitSurveyReply, updateFitSurvey, getCustomerFits, newsfeed: { fits } } = this.props;
    const {
      value,
      parentNode: { dataset: { stockid: stockId, asin } },
      dataset: { selected }
    } = currentTarget;

    const fitSelected = fits.find(item => item.asin === asin);
    this.setState({ isFitSurveyLoading: true });
    // the customer has already selected a fit survey for this item
    if (fitSelected) {
      if (selected === 'true') {
        // if you're clicking an already selected fit, then remove it
        updateFitSurvey({ stockId, id: fitSelected.id, deleteFit: true }, value)
          .then(() => {
            getCustomerFits()
              .then(() => {
                this.setState({ isFitSurveyLoading: false });
              });
          });
      } else {
        // otherwise, select the new fit
        updateFitSurvey({ stockId, id: fitSelected.id }, value)
          .then(() => {
            getCustomerFits()
              .then(() => {
                this.setState({ isFitSurveyLoading: false });
              });
          });
      }
    // the customer has never selected a fit for this asin before
    } else {
      fitSurveyReply(stockId, value)
        .then(() => {
          getCustomerFits()
            .then(() => {
              this.setState({ isFitSurveyLoading: false });
            });
        });
    }

  }

  showCancelItemsMobile = () => {
    window.scrollTo(0, 0);
    this.setState({ cancelItemsMobileVisible: true });
  }

  hideCancelItemsMobile = () => {
    this.setState({ cancelItemsMobileVisible: false });
  }

  showReturnItemsMobile = () => {
    window.scrollTo(0, 0);
    this.setState({ returnItemsMobileVisible: true });
  }

  hideReturnItemsMobile = () => {
    this.setState({ returnItemsMobileVisible: false });
  }

  makeOrderCareOrDetails() {
    const { order: { order } } = this.props;
    const { customerActionRequired, paymentMethod, shippingAddress } = order;
    const { sameOrderPaymentUpdated } = this.state;

    return customerActionRequired ?
      <OrderCare /> :
      <OrderInformation
        order={order}
        payment={paymentMethod}
        address={shippingAddress}
        paymentUpdated={sameOrderPaymentUpdated} />;
  }

  render() {
    const {
      account: { cancelOrReturnItems: items },
      order: { order },
      newsfeed: { fits },
      isLoading,
      params,
      hydra6pmReturns,
      hydraZenReturns,
      hydraOrderDetailFitSurvey
    } = this.props;
    const staleData = order && (params.orderId !== order.orderId);
    const { testId, marketplace: { account: { martyReturns } } } = this.context;
    const {
      isFitSurveyLoading,
      sameOrderPaymentUpdated,
      cancelItemsMobileVisible,
      returnItemsMobileVisible
    } = this.state;

    if (isLoading || !order || staleData) {
      return <PageLoader centered={true}/>;

    } else if (cancelItemsMobileVisible) {
      return <CancelOrReturnItemSelect
        type="cancel"
        order={order}
        selectedItems={items}
        onHideItemSelect={this.hideCancelItemsMobile}
        onToggleItemSelect={this.handleToggleItemSelection}
        onSubmit={this.handleCancelItems} />;

    } else if (returnItemsMobileVisible) {
      return <CancelOrReturnItemSelect
        type="return"
        order={order}
        selectedItems={items}
        onHideItemSelect={this.hideReturnItemsMobile}
        onToggleItemSelect={this.handleToggleItemSelection}
        onSubmit={(martyReturns || hydra6pmReturns) ? this.handleReturnItems : this.handleReturnItemsLegacy} />;

    } else {
      const { orderId, orderDate } = order;
      const breadcrumbItems = [
        { name: 'Order History', url: '/orders' },
        { name: `Order #${orderId}` }
      ];
      return (
        <SiteAwareMetadata className={css.orderDetailHeader}>
          <div className={css.orderDetail}>
            <Breadcrumb items={breadcrumbItems}/>
            <div className={css.accountHeader}>
              <AccountHeader title="Order Information"/>
            </div>
            <div className={css.backNav}>
              <BackNav to="/orders">Order History</BackNav>
            </div>
            <div className={css.orderInfo}> {/* @todo consolidate this https://github01.zappos.net/mweb/marty/issues/5952 */}
              {sameOrderPaymentUpdated && <p className={css.paymentUpdateSuccess}><strong>Success!</strong> Your card has been updated. We're processing your order now.</p>}
              <div>
                <span className={css.orderDate} data-test-id={testId('orderDate')}>
                  {moment(orderDate, MY_ACCOUNT_DATE_FORMAT).format('ll')}
                </span>
                <span>
                  <OrderStatus order={order}/>
                </span>
              </div>
              <div>
                <span className={css.orderId} data-test-id={testId('orderId')}>#{orderId}</span>
              </div>
            </div>
            {this.makeOrderCareOrDetails()}

            <OrderTableLayout
              order={order}
              isFitSurveyLoading={isFitSurveyLoading}
              showZenReturns={hydraZenReturns}
              fitItems={fits}
              showFitSurvey={hydraOrderDetailFitSurvey} // use the `showFitSurvey` marketplace variable once "orfs" becomes permanent
              onSelectFitQuestion={this.handleSelectFitQuestion}
              onToggleItemSelect={this.handleToggleItemSelection} />

            <OrderSummary
              order={order}
              selectedItems={items}
              onCancelItems={this.handleCancelItems}
              onReturnItems={(martyReturns || hydra6pmReturns) ? this.handleReturnItems : this.handleReturnItemsLegacy}
              showCancelItemsMobile={this.showCancelItemsMobile}
              showReturnItemsMobile={this.showReturnItemsMobile}
              showZenReturns={hydraZenReturns} />
            <ReturnsAndExchanges/>
          </div>
        </SiteAwareMetadata>
      );
    }
  }
}

const mapStateToProps = state => ({
  account: state.account,
  order: state.order,
  returns: state.returns,
  newsfeed: state.newsfeed,
  routing: state.routing,
  shipmentTracking: state.shipmentTracking,
  hydraZenReturns: isAssigned(HYDRA_ZEN_RETURNS, 1, state),
  hydra6pmReturns: isAssigned(HYDRA_6PM_MARTY_RETURNS, 1, state),
  hydraOrderDetailFitSurvey: isAssigned(HYDRA_ORDER_DETAIL_FIT_SURVEY, 1, state)
});

export default connect(mapStateToProps, {
  checkCustomerAuthentication,
  fetchOrderDetails,
  firePixelServer,
  fitSurveyReply,
  getCustomerFits,
  redirectToLegacyReturns,
  removeItemForCancellationOrReturn,
  resetCancelOrReturnItems,
  resetShipmentTracking,
  selectItemForCancellationOrReturn,
  stepSelection,
  triggerAssignment,
  updateBoxNumInfo,
  updateFitSurvey
})(Order);
